import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, Ticket, RefreshCw } from "lucide-react";

const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentStatus() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const intervalRef = useRef(null);

  const fetchStatus = async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const { data } = await axios.get(`${apiBase}/public/orders/${orderId}/status`);
      setData(data);
      setErr(null);
      return data;
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur");
      return null;
    } finally {
      if (manual) setRefreshing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    fetchStatus();
    // Poll every 2 seconds while pending. Stops as soon as status is final.
    intervalRef.current = setInterval(async () => {
      if (cancelled) return;
      setAttempts((a) => a + 1);
      const d = await fetchStatus();
      if (d && (d.status === "paid" || d.status === "failed" || d.status === "canceled")) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 2000);
    // Safety: stop polling after 2 minutes max
    const stopTimer = setTimeout(() => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }, 120000);
    return () => { cancelled = true; clearTimeout(stopTimer); if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [orderId]);

  const status = data?.status;
  const isPaid = status === "paid";
  const isFailed = status === "failed" || status === "canceled";
  const isPending = data && !isPaid && !isFailed;

  return (
    <div className="min-h-screen bg-[#F4F5F7] flex items-center justify-center p-4">
      <div className="bg-white border border-[#E0E0E0] rounded-lg p-8 max-w-md w-full text-center" data-testid="payment-status">
        {!data && !err && (
          <>
            <Loader2 className="w-12 h-12 text-[#0A4628] mx-auto mb-3 animate-spin" />
            <p className="font-display text-xl font-bold">Vérification du paiement…</p>
            <p className="text-sm text-[#616161] mt-2">Confirmation instantanée avec FedaPay.</p>
          </>
        )}
        {err && (
          <>
            <XCircle className="w-12 h-12 text-[#C62828] mx-auto mb-3" />
            <p className="font-display text-xl font-bold text-[#C62828]">Commande introuvable</p>
            <p className="text-sm text-[#616161] mt-2">{err}</p>
          </>
        )}
        {data && isPaid && (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#2E7D32] mx-auto mb-3" />
            <p className="font-display text-2xl font-extrabold text-[#2E7D32]">Paiement réussi !</p>
            <p className="text-sm text-[#616161] mt-2">
              {data.quantity} billet{data.quantity > 1 ? "s" : ""} · Total : <strong>{(data.total_xof || 0).toLocaleString("fr-FR")} FCFA</strong>
            </p>
            <p className="text-xs text-[#616161] mt-1">Un email de confirmation vous a été envoyé.</p>
            {data.tickets?.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-xs tracking-overline text-[#616161]">Vos billets :</p>
                {data.tickets.map((t) => (
                  <Link key={t.id} to={`/billetterie/ticket/${t.id}`}
                    className="block bg-[#0A4628] text-white rounded p-3 hover:bg-[#0D5934] transition font-mono text-sm" data-testid={`ticket-link-${t.id}`}>
                    <Ticket className="w-4 h-4 inline mr-1.5" /> {t.code}
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
        {data && isFailed && (
          <>
            <XCircle className="w-14 h-14 text-[#C62828] mx-auto mb-3" />
            <p className="font-display text-2xl font-extrabold text-[#C62828]">Paiement non abouti</p>
            <p className="text-sm text-[#616161] mt-2">Votre paiement a été annulé ou refusé. Aucun montant n&apos;a été débité.</p>
          </>
        )}
        {isPending && (
          <>
            <Loader2 className="w-12 h-12 text-[#FBC02D] mx-auto mb-3 animate-spin" />
            <p className="font-display text-xl font-bold">Paiement en cours…</p>
            <p className="text-sm text-[#616161] mt-2">
              Vérification automatique toutes les 2 secondes
              {attempts > 0 && <span className="text-[#9E9E9E]"> · {attempts} tentative{attempts > 1 ? "s" : ""}</span>}
            </p>
            <button
              onClick={() => fetchStatus(true)}
              disabled={refreshing}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#E0E0E0] hover:bg-[#F4F5F7] text-sm font-medium disabled:opacity-50"
              data-testid="manual-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Vérification…" : "Vérifier maintenant"}
            </button>
            <p className="text-[10px] text-[#9E9E9E] mt-4 leading-relaxed">
              Si vous venez de payer par Mobile Money, votre opérateur peut prendre quelques secondes.
              Ne fermez pas cette page.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
